// imageUpload.js — pure, unit-testable helpers for the v2 ImageEditor upload path
// (WL-011c). Kept dependency-free (no react/redux/api) so the presentational
// ImageEditor and its adapter (ImageEditorSettings) share ONE validation source
// and the unit test can import it directly.
//
// Mirrors the legacy image upload gate in
// components/SegmentBuilder/Shared/UploadMedia/index.jsx:
//   - allowedMimeTypes.image extensions
//   - the 50MB per-file cap
// so the v2 editor rejects exactly the files the legacy editor rejected.

// Extensions the legacy UploadMedia accepts for `image`.
export const ALLOWED_IMAGE_EXT = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "heic",
  "svg",
  "webp",
  "avif",
];

// <input accept="…"> value — matches legacy getAcceptedFileTypes("image").
export const IMAGE_ACCEPT =
  "image/png, image/jpeg, image/gif, image/heic, image/svg+xml, image/webp, image/avif";

// Legacy cap: file.size / 1_000_000 > 50 (decimal MB, matching UploadMedia).
export const MAX_UPLOAD_MB = 50;

export function fileExtension(name = "") {
  const parts = String(name).split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

export function isExtensionAllowed(name, allowed = ALLOWED_IMAGE_EXT) {
  return allowed.includes(fileExtension(name));
}

// Returns an error string when the file must be rejected, else null.
// gifAllowed=false additionally rejects .gif (parity with callers that disallow gifs).
export function validateImageFile(file, { gifAllowed = true } = {}) {
  if (!file) return "No file selected.";
  const ext = fileExtension(file.name);
  const allowed = gifAllowed
    ? ALLOWED_IMAGE_EXT
    : ALLOWED_IMAGE_EXT.filter((e) => e !== "gif");
  if (!allowed.includes(ext)) {
    return "That file type is not supported.";
  }
  if (file.size / 1000000 > MAX_UPLOAD_MB) {
    return `${file.name} cannot be uploaded. File size cannot be greater than ${MAX_UPLOAD_MB}MB.`;
  }
  return null;
}
